<template>
  <div>
    <page-with-sidebar>
      <div slot="sidebar">
        <customer-sidebar :loading="sidebarLoading"
                          :customer-accounts="userAccounts"
                          :block-selection="sidebarSelectionBlocked"
                          @select-account="handleMenuItemSelect"
                          :active-item="hasCustomerId || customerId"/>
      </div>
      <page-layout>
        <section slot="header">
          <transition name="fade" mode="out-in">
            <header-customer-data v-if="Object.keys(customerData).length"
                                  :key="'header' + this.headerKey"
                                  :customer-info="customerData"/>
            <div v-else
                 :key="'headerPlaceholder'"
                 class="header-placeholder"></div>
          </transition>
        </section>
        <section class="offer-content" key="offer-content">
          <transition name="fade" mode="out-in">
            <loader-with-message v-if="dataLoading"
                                 :has-error="dataFetchError"
                                 @retry="fetchPersonalizedOffer"
                                 key="dataLoading"/>
            <div v-else key="offersLoading">
              <base-heading>{{ $t('personalizedOffer.offerForYou') }}</base-heading>
              <div class="offer-wrap">
                <template v-if="offers.length">
                  <personalized-banner v-for="(offer, index) in offers"
                                       :key="offer.product.externalId + index"
                                       :image="(offer.product.images && offer.product.images.length) ? offer.product.images[0].url : ''"
                                       :offerType="offer.product.productType"
                                       @activate-offer="openAddonModal(offer)"
                                       @sign-up-for-offer="openModal(offer)"/>
                </template>
                <template v-else>
                  <div class="no-offers">
                    <img src="/assets/images/no-results.svg" width="112" height="112" alt=""/>
                    <h3 class="title">{{ $t('personalizedOffer.noResultsTitle') }}</h3>
                  </div>
                </template>
              </div>
            </div>
          </transition>
        </section>
      </page-layout>
    </page-with-sidebar>

    <confirmation-modal :visible="confirmationModalVisible"
                        :loading="confirmationLoader"
                        :closeButtonVisible="false"
                        @close="confirmationModalVisible = false"
                        cancel-button
                        confirm-type="empty"
                        :dynamic-confirm-title="Object.keys(offerData).length ? $t('personalizedOffer.confirmationModal.signUpTitle', {offerName: offerData.product.name} ) : ''"
                        :confirm-description="$t('personalizedOffer.confirmationModal.signUpDescription' )"
                        :confirm-text="$t('common.signUp' )"
                        @confirm="activatePersonalizedOffer"/>

    <confirmation-modal :visible="confirmationAddonsModalVisible"
                        :loading="confirmationLoader"
                        :closeButtonVisible="false"
                        @close="confirmationAddonsModalVisible = false"
                        cancel-button
                        :button-disabled="dataLoading"
                        confirm-type="empty"
                        :dynamic-confirm-title="Object.keys(offerData).length ? $t('personalizedOffer.confirmationModal.activateTitle', {offerName: offerData.product.name} ) : ''"
                        :confirm-description="$t('personalizedOffer.confirmationModal.activateDescription' )"
                        :confirm-text="$t('common.activate' )"
                        @confirm="activateAddonsApi"/>
  </div>
</template>

<script>
import PageWithSidebar from '@/components/common/main-layout/page-with-sidebar'
import PageLayout from '@/components/common/main-layout/page-layout'
import HeaderCustomerData from '@/components/common/headers/header-customer-data'
import LoaderWithMessage from '@/components/common/loader-with-message'
import PersonalizedBanner from '@/components/banners/personalized-banner'
import CustomerSidebar from '@/components/common/navigation/customer-sidebar'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import { lsGet, lsRemove } from '@/utils/localStorageController'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import { addUserIdDataLayerEvent } from '@/utils/addUserIdDataLayerEvent'

export default {
  name: 'page-personalized-offer',
  components: {
    PersonalizedBanner,
    PageWithSidebar,
    PageLayout,
    LoaderWithMessage,
    CustomerSidebar,
    HeaderCustomerData,
    ConfirmationModal
  },
  data () {
    return {
      sidebarLoading: true,
      dataLoading: true,
      dataFetchError: false,
      userAccounts: [],
      customerId: '',
      offers: [],
      sidebarSelectionBlocked: false,
      customerData: [],
      headerKey: '',
      confirmationModalVisible: false,
      confirmationAddonsModalVisible: false,
      hasCustomerId: lsGet('sc_customer_id'),
      offerData: [],
      confirmationLoader: false
    }
  },
  mixins: [errorMessageHandler],
  methods: {
    openModal (offerData) {
      this.confirmationModalVisible = true
      this.offerData = offerData
    },
    openAddonModal (offerData) {
      this.confirmationAddonsModalVisible = true
      this.offerData = offerData
    },
    activateAddonsApi () {
      this.confirmationLoader = true
      const dataToSend = {
        subscriberIdentity: this.offerData.subscriberIdentity,
        addonExternalId: this.offerData.product.externalId,
        addonTargetType: 'PERSONALIZED_OFFER',
        activate: true
      }
      this.$apiRequest.post('user/services/addons', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.confirmationLoader = false
            this.confirmationAddonsModalVisible = false
            this.fetchPersonalizedOffer()
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successAddonActivationMessage')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
          this.confirmationLoader = false
        })
    },
    activatePersonalizedOffer () {
      this.confirmationLoader = true
      const dataToSend = {
        customerId: this.customerId,
        offerCode: this.offerData.product.code
      }
      this.$apiRequest.post('user/offers', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.confirmationLoader = false
            this.confirmationModalVisible = false
            this.fetchPersonalizedOffer()
            this.toast('success', {
              title: this.$t('toastMessages.successPersonalizedOfferActivationTitle'),
              message: this.$t('toastMessages.successPersonalizedOfferActivation')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
          this.confirmationLoader = false
        })
    },
    handleMenuItemSelect (menuPayload) {
      lsRemove('sc_customer_id')
      this.hasCustomerId = null
      this.customerId = menuPayload.subscriptionInfo.customerId
      this.customerData = menuPayload.subscriptionInfo
      this.headerKey = new Date().getTime()
      this.fetchPersonalizedOffer()
    },
    async fetchUserInfo () {
      try {
        const response = await this.$apiRequest.get('user/info')
        this.userAccounts = response.data.mtmCustomers || []
        this.customerId = response.data.mtmCustomers[0].subscriptionInfo.customerId
        this.sidebarLoading = false
      } catch (e) {
        this.handleErrorMessage(e.response.data)
      }
    },
    fetchPersonalizedOffer () {
      this.dataLoading = true
      const customer = this.hasCustomerId ? this.hasCustomerId : this.customerId
      this.$apiRequest.get(`user/offers?customerId=${customer}`)
        .then(response => {
          this.offers = response.data.personalizedOffer || []
          this.dataLoading = false
        })
        .catch(e => {
          this.dataLoading = false
          this.offers = []
        })
    },
    async fetchData () {
      await this.fetchUserInfo()
      this.fetchPersonalizedOffer()
    }
  },
  created () {
    this.fetchData()
    addUserIdDataLayerEvent()
  }
}
</script>

<style scoped lang="scss">
.offer-content {
  padding: 2rem;

  @media (map_get($breakpoints, sm)) {
    padding-top: 4rem;
  }
}

.no-offers {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  color: $dark;
  padding-top: 4rem;
  text-align: center;
  width: 100%;

  @media (map_get($breakpoints, sm)) {
    padding-top: 14rem;
  }

  img {
    display: inline-block;
    width: 112px;
    height: auto;
    margin: 0 auto 2.4rem;
  }

  .title {
    font-size: 3.2rem;
    font-weight: normal;
    margin-bottom: 4rem;
  }

  .description {
    font-size: 1.6rem;
  }
}

.offer-wrap {
  @media (map_get($breakpoints, exsm)) {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -.8rem;
  }
}
</style>
